import { useOfflineFirstQuery } from '../../../hooks/useOfflineFirstQuery'
import { apiClient } from '../../../api/client'

type ComplianceSummary = {
  audit_packets_generated: number
  incidents_open: number
  retention_checks_pending: number
  unsigned_notes: number
}

export default function ComplianceHomePage() {
  const { data, isFetching, error, refetch } = useOfflineFirstQuery<ComplianceSummary>({
    queryKey: ['compliance-summary'],
    cacheKey: 'compliance:summary',
    staleTime: 1000 * 60,
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/dashboard/stats/')
      const compliance = res.data?.compliance ?? {}
      return {
        audit_packets_generated: compliance.audit_packets_generated ?? 0,
        incidents_open: compliance.incidents_open ?? 0,
        retention_checks_pending: compliance.retention_checks_pending ?? 0,
        unsigned_notes: compliance.unsigned_notes ?? compliance.pending_signatures ?? 0,
      }
    },
  })

  const cards = [
    { label: 'Unsigned Notes', value: data?.unsigned_notes ?? 0 },
    { label: 'Open Incidents', value: data?.incidents_open ?? 0 },
    { label: 'Retention Checks Pending', value: data?.retention_checks_pending ?? 0 },
    { label: 'Audit Packets Generated', value: data?.audit_packets_generated ?? 0 },
  ]

  const handleExport = async () => {
    await apiClient.post('/api/v1/compliance/audit-packet/')
    await refetch()
  }

  const handleRetention = async () => {
    await apiClient.post('/api/v1/compliance/retention/evaluate/')
    await refetch()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Compliance</h1>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
          >
            Export Audit Packet
          </button>
          <button
            onClick={handleRetention}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Run Retention Check
          </button>
          <button
            onClick={() => refetch()}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {(error as Error).message}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{isFetching ? '...' : card.value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
